import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Stack,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  FamilyRestroom as FamilyIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Timeline,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Format dates for display (MM/DD/YYYY)
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return '';
  
  // Parse YYYY-MM-DD HH:mm:ss format
  const [datePart] = dateStr.split(' ');
  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Get date range title
const getDateRangeTitle = (startDate, endDate, dateRange) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Parse YYYY-MM-DD safely to avoid timezone issues
  const parseYMD = (str) => {
    if (!str) return null;
    const [year, month, day] = str.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const start = parseYMD(startDate);
  const end = parseYMD(endDate);

  // Format date as "MMM d, yyyy" (e.g., "Jan 1, 2024")
  const formatDate = (date) => {
    return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  };

  // Format month as "MMM yyyy" (e.g., "Jan 2024")
  const formatMonth = (date) => {
    return date ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
  };

  // Helper function to compare dates ignoring time
  const isSameDay = (date1, date2) => {
    return date1 && date2 &&
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  switch (dateRange) {
    case 'day':
      if (isSameDay(end, today)) {
        return 'Today';
      } else if (isSameDay(end, yesterday)) {
        return 'Yesterday';
      } else {
        return formatDate(end);
      }
    case 'week': {
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - today.getDay());
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(thisWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

      if (isSameDay(end, thisWeekEnd)) {
        return 'This Week';
      } else if (isSameDay(end, lastWeekEnd)) {
        return 'Last Week';
      } else {
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
    }
    case 'month': {
      const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

      if (end && end.getFullYear() === thisMonthEnd.getFullYear() && end.getMonth() === thisMonthEnd.getMonth()) {
        return 'This Month';
      } else if (end && end.getFullYear() === lastMonthEnd.getFullYear() && end.getMonth() === lastMonthEnd.getMonth()) {
        return 'Last Month';
      } else {
        return formatMonth(end);
      }
    }
    default:
      return `${formatDate(start)} - ${formatDate(end)}`;
  }
};

// Stat Card Component
const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 100, sm: 140 },
        bgcolor: isDark ? 'background.paper' : 'white',
        transition: theme.transitions.create(['background-color'], {
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography 
          component="h2" 
          variant="h6" 
          gutterBottom
          sx={{ 
            color: color,
            fontSize: { xs: '1rem', sm: '1.1rem' }
          }}
        >
          {title}
        </Typography>
        <Box sx={{ 
          color: color,
          '& svg': {
            fontSize: { xs: '1.8rem', sm: '1.5rem' }
          }
        }}>
          {icon}
        </Box>
      </Box>
      <Typography 
        component="p" 
        variant="h4" 
        color="text.primary"
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.5rem' }
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
};

const IndividualNumbers = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Get first and last day of the current month
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('submitDate');
  const [order, setOrder] = useState('desc');
  const [dateRange, setDateRange] = useState('month');
  const [filters, setFilters] = useState({
    dateType: 'submit',
    startDate: formatDateForInput(firstDayOfMonth),
    endDate: formatDateForInput(lastDayOfMonth),
    carrier: '',
    product: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    carriers: [],
    products: [],
  });

  // Fetch individual user stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get('/api/individual/stats', {
          headers: { 'x-auth-token': token },
          params: filters
        });
        
        setStats(response.data);
        
        // Extract unique values for filters
        if (response.data.closes) {
          const carriers = [...new Set(response.data.closes.map(close => close.carrier))].filter(Boolean);
          const products = [...new Set(response.data.closes.map(close => close.product))].filter(Boolean);
          
          setFilterOptions({
            carriers,
            products,
          });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch individual stats:', err);
        setError('Failed to load your performance data. Please try again later.');
        setLoading(false);
      }
    };

    fetchStats();
  }, [filters, navigate]);

  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle date range changes
  const handleDateRangeChange = (range) => {
    let startDate, endDate;
    const today = new Date();
    
    switch (range) {
      case 'day':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'week':
        // Start of week (Sunday)
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        // End of week (Saturday)
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        break;
      case 'month':
        // Start of month
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        // End of month
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      default:
        return;
    }
    
    setDateRange(range);
    setFilters(prev => ({
      ...prev,
      startDate: formatDateForInput(startDate),
      endDate: formatDateForInput(endDate)
    }));
  };

  // Handle date shifting (next/previous day, week, month)
  const handleDateShift = (direction) => {
    const currentStartDate = new Date(filters.startDate);
    const currentEndDate = new Date(filters.endDate);
    let newStartDate, newEndDate;
    
    switch (dateRange) {
      case 'day':
        newStartDate = new Date(currentStartDate);
        newEndDate = new Date(currentEndDate);
        newStartDate.setDate(currentStartDate.getDate() + (direction === 'back' ? -1 : 1));
        newEndDate.setDate(currentEndDate.getDate() + (direction === 'back' ? -1 : 1));
        break;
      case 'week':
        newStartDate = new Date(currentStartDate);
        newEndDate = new Date(currentEndDate);
        newStartDate.setDate(currentStartDate.getDate() + (direction === 'back' ? -7 : 7));
        newEndDate.setDate(currentEndDate.getDate() + (direction === 'back' ? -7 : 7));
        break;
      case 'month':
        newStartDate = new Date(currentStartDate);
        newStartDate.setMonth(currentStartDate.getMonth() + (direction === 'back' ? -1 : 1));
        newEndDate = new Date(newStartDate.getFullYear(), newStartDate.getMonth() + 1, 0);
        break;
      default:
        return;
    }
    
    setFilters(prev => ({
      ...prev,
      startDate: formatDateForInput(newStartDate),
      endDate: formatDateForInput(newEndDate)
    }));
  };

  // Handle sort changes
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort function for close entries
  const sortCloses = (data) => {
    if (!data) return [];
    
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // Convert date strings to Date objects for comparison
      if (orderBy === 'submitDate' || orderBy === 'effectiveDate') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }
      
      // Convert AP to numbers
      if (orderBy === 'ap') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
      
      if (order === 'desc') {
        return bValue > aValue ? 1 : -1;
      } else {
        return aValue > bValue ? 1 : -1;
      }
    });
  };

  // Handle clearing all filters
  const handleClearFilters = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters({
      dateType: 'submit',
      startDate: formatDateForInput(firstDayOfMonth),
      endDate: formatDateForInput(lastDayOfMonth),
      carrier: '',
      product: '',
    });
    setDateRange('month');
  };

  // Filter closes by search query
  const getFilteredCloses = () => {
    if (!stats || !stats.closes) return [];
    
    const sortedCloses = sortCloses(stats.closes);
    
    if (!searchQuery) return sortedCloses;
    
    const query = searchQuery.toLowerCase();
    return sortedCloses.filter(close => 
      close.carrier?.toLowerCase().includes(query) ||
      close.product?.toLowerCase().includes(query) ||
      close.status?.toLowerCase().includes(query)
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pt: { xs: 3, sm: 1 },
      }}>
        <Typography variant="h4" gutterBottom>
          My Performance
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ mr: 2, fontWeight: 'medium' }}>
            {getDateRangeTitle(filters.startDate, filters.endDate, dateRange)}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleDateShift('back')}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            
            <ToggleButtonGroup
              value={dateRange}
              exclusive
              onChange={(e, value) => value && handleDateRangeChange(value)}
              size="small"
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="day">Day</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>
            
            <IconButton
              size="small"
              onClick={() => handleDateShift('forward')}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
      
      {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
      
      {/* Filter controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Type</InputLabel>
              <Select
                name="dateType"
                value={filters.dateType}
                label="Date Type"
                onChange={handleFilterChange}
              >
                <MenuItem value="submit">Submit Date</MenuItem>
                <MenuItem value="effective">Effective Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Carrier</InputLabel>
              <Select
                name="carrier"
                value={filters.carrier}
                label="Carrier"
                onChange={handleFilterChange}
              >
                <MenuItem value="">All Carriers</MenuItem>
                {filterOptions.carriers.map(carrier => (
                  <MenuItem key={carrier} value={carrier}>
                    {carrier}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Product</InputLabel>
              <Select
                name="product"
                value={filters.product}
                label="Product"
                onChange={handleFilterChange}
              >
                <MenuItem value="">All Products</MenuItem>
                {filterOptions.products.map(product => (
                  <MenuItem key={product} value={product}>
                    {product}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              fullWidth
              sx={{
                height: '40px',
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Stats */}
      {stats && (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Production"
              value={formatCurrency(stats.totalAP || 0)}
              icon={<TrendingUpIcon />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Average AP"
              value={formatCurrency(stats.totalCloses ? (stats.totalAP / stats.totalCloses) : 0)}
              icon={<MoneyIcon />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Families Protected"
              value={stats.totalCloses || 0}
              icon={<FamilyIcon />}
              color="#ed6c02"
            />
          </Grid>
        </Grid>
      )}

      {/* Carrier and Product Breakdowns */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Carrier Breakdown
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Carrier</TableCell>
                      <TableCell align="right">Policies</TableCell>
                      <TableCell align="right">AP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.carrierStats?.map((carrier) => (
                      <TableRow key={carrier.carrier}>
                        <TableCell>{carrier.carrier}</TableCell>
                        <TableCell align="right">{carrier.closes}</TableCell>
                        <TableCell align="right">{formatCurrency(carrier.totalAP)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Product Breakdown
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Policies</TableCell>
                      <TableCell align="right">AP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.productStats?.map((product) => (
                      <TableRow key={product.product}>
                        <TableCell>{product.product}</TableCell>
                        <TableCell align="right">{product.closes}</TableCell>
                        <TableCell align="right">{formatCurrency(product.totalAP)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Policy List */}
      <Typography variant="h5" gutterBottom color="text.primary" sx={{ mt: 4, mb: 2 }}>
        My Policies
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search policies by carrier, product, or status..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      </Box>

      <TableContainer 
        component={Paper}
        sx={{
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: 650, // Ensures table has minimum width
          },
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap', // Prevents text wrapping in cells
          }
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'carrier'}
                  direction={orderBy === 'carrier' ? order : 'asc'}
                  onClick={() => handleSort('carrier')}
                >
                  Carrier
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'product'}
                  direction={orderBy === 'product' ? order : 'asc'}
                  onClick={() => handleSort('product')}
                >
                  Product
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'submitDate'}
                  direction={orderBy === 'submitDate' ? order : 'asc'}
                  onClick={() => handleSort('submitDate')}
                >
                  Submit Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'effectiveDate'}
                  direction={orderBy === 'effectiveDate' ? order : 'asc'}
                  onClick={() => handleSort('effectiveDate')}
                >
                  Effective Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'ap'}
                  direction={orderBy === 'ap' ? order : 'asc'}
                  onClick={() => handleSort('ap')}
                >
                  AP
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredCloses().map((close, index) => (
              <TableRow key={index}>
                <TableCell>{close.carrier}</TableCell>
                <TableCell>{close.product}</TableCell>
                <TableCell>{formatDateForDisplay(close.submitDate)}</TableCell>
                <TableCell>{formatDateForDisplay(close.effectiveDate)}</TableCell>
                <TableCell align="right">{formatCurrency(close.ap)}</TableCell>
                <TableCell>
                  <Chip 
                    label={close.status} 
                    size="small"
                    color={close.status === 'Issued' ? 'success' : close.status === 'Pending' ? 'warning' : 'default'} 
                    variant="outlined" 
                  />
                </TableCell>
              </TableRow>
            ))}
            {getFilteredCloses().length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">No policies found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default IndividualNumbers; 